/**
 * externalExpensesHelper.ts — Gastos externos del dueño (localStorage).
 *
 * Egresos pagados fuera de la gaveta (Nequi, tarjeta personal, etc.) que impactan
 * utilidad neta del día sin alterar arqueo físico. Persistencia ACTIVO / ANULADO.
 */

import { getDefaultPaymentMethodLabel } from "@/lib/paymentMethodsConfig";
import {
  runTransaction,
  txRead,
  txWrite,
} from "@/lib/persistence/transactionalStore";

export type GastoExternoEstado = "ACTIVO" | "ANULADO";

/** Slot del método de pago (mismos ids que POS) o valores legacy. */
export type GastoExternoMetodo = string;

export interface GastoExterno {
  id: string;
  /** Fecha contable YYYY-MM-DD (puede ser retroactiva). */
  fechaContable: string;
  monto: number;
  categoria: string;
  descripcion: string;
  metodoPago: GastoExternoMetodo;
  /** Etiqueta snapshot al registrar (si el dueño renombra el método después). */
  metodoPagoLabel?: string;
  estado: GastoExternoEstado;
  createdAt: string;
  updatedAt: string;
  anuladoEn?: string;
  motivoAnulacion?: string;
  usuarioId: string;
  userName?: string;
}

export const LS_GASTOS_EXTERNOS = "caja_gastos_externos";

/** Alineadas con motivos de gasto en CajaMovimientos + gastos estructurales. */
export const GASTO_EXTERNO_CATEGORIAS = [
  "Pago a proveedor",
  "Compra de insumos",
  "Transporte",
  "Servicios",
  "Mantenimiento",
  "Arriendo",
  "Servicios públicos",
  "Nómina externa",
  "Otro",
] as const;

export const GASTO_EXTERNO_METODOS: Array<{
  id: GastoExternoMetodo;
  label: string;
}> = [
  { id: "efectivo_personal", label: "Efectivo personal (no sale de caja)" },
  { id: "nequi", label: "Nequi" },
  { id: "daviplata", label: "Daviplata" },
  { id: "tarjeta", label: "Tarjeta" },
  { id: "transferencia", label: "Transferencia bancaria" },
  { id: "otro", label: "Otro" },
];

const LEGACY_METODO_LABELS: Record<string, string> = {
  efectivo_personal: "Efectivo personal (no sale de caja)",
  tarjeta_personal: "Tarjeta personal",
  transferencia: "Transferencia bancaria",
  otro: "Otro",
};

const LEGACY_SLOT_FOR_LABEL: Record<string, string> = {
  nequi: "digital_1",
  daviplata: "digital_2",
  transferencia: "digital_3",
  tarjeta_personal: "tarjeta",
};

export const gastoExternoMetodoLabel = (
  id: GastoExternoMetodo,
  labelSnapshot?: string,
): string => {
  if (labelSnapshot?.trim()) return labelSnapshot.trim();
  if (id === "efectivo_personal") {
    return LEGACY_METODO_LABELS.efectivo_personal;
  }
  const slot = LEGACY_SLOT_FOR_LABEL[id] ?? id;
  return getDefaultPaymentMethodLabel(slot);
};

/** YYYY-MM-DD desde ISO o fecha local. */
export const fechaContableFromIso = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const readAllRaw = (): GastoExterno[] => {
  const parsed = txRead<GastoExterno[]>(LS_GASTOS_EXTERNOS, []);
  return Array.isArray(parsed) ? parsed : [];
};

/** Persiste la lista dentro de la transacción activa (no emite eventos). */
const persistAll = (list: GastoExterno[]): void => {
  txWrite(LS_GASTOS_EXTERNOS, list);
};

/** Notifica a la UI. Debe emitirse SOLO tras el commit durable. */
const emitGastosUpdated = (): void => {
  window.dispatchEvent(new CustomEvent("caja-gastos-externos-updated"));
};

export const readGastosExternos = (): GastoExterno[] =>
  readAllRaw().sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

export const readGastosExternosByFecha = (
  fechaContable: string,
): GastoExterno[] =>
  readGastosExternos().filter((g) => g.fechaContable === fechaContable);

/** Suma solo gastos ACTIVOS de una fecha contable. */
export const sumGastosExternosActivos = (fechaContable: string): number =>
  readGastosExternosByFecha(fechaContable)
    .filter((g) => g.estado === "ACTIVO")
    .reduce((s, g) => s + Math.max(0, Math.round(g.monto)), 0);

export const readGastosExternosActivosByFecha = (
  fechaContable: string,
): GastoExterno[] =>
  readGastosExternosByFecha(fechaContable).filter((g) => g.estado === "ACTIVO");

export interface CreateGastoExternoInput {
  fechaContable: string;
  monto: number;
  categoria: string;
  descripcion: string;
  metodoPago: GastoExternoMetodo;
  metodoPagoLabel?: string;
  usuarioId: string;
  userName?: string;
}

export const createGastoExterno = (
  input: CreateGastoExternoInput,
): GastoExterno => {
  const now = new Date().toISOString();
  const gasto: GastoExterno = {
    id: `GEXT-${Date.now()}`,
    fechaContable: input.fechaContable,
    monto: Math.max(0, Math.round(input.monto)),
    categoria: input.categoria.trim(),
    descripcion: input.descripcion.trim(),
    metodoPago: input.metodoPago,
    metodoPagoLabel: input.metodoPagoLabel?.trim(),
    estado: "ACTIVO",
    createdAt: now,
    updatedAt: now,
    usuarioId: input.usuarioId,
    userName: input.userName,
  };
  runTransaction((): void => {
    persistAll([gasto, ...readAllRaw()]);
  });
  emitGastosUpdated();
  return gasto;
};

export interface UpdateGastoExternoInput {
  monto?: number;
  categoria?: string;
  descripcion?: string;
  metodoPago?: GastoExternoMetodo;
  metodoPagoLabel?: string;
  fechaContable?: string;
}

export const updateGastoExterno = (
  id: string,
  patch: UpdateGastoExternoInput,
): GastoExterno | null => {
  const updated = runTransaction((): GastoExterno | null => {
    const list = readAllRaw();
    const idx = list.findIndex((g) => g.id === id);
    if (idx === -1) return null;
    if (list[idx].estado === "ANULADO") return null;

    const next: GastoExterno = {
      ...list[idx],
      ...patch,
      monto:
        patch.monto !== undefined
          ? Math.max(0, Math.round(patch.monto))
          : list[idx].monto,
      categoria: patch.categoria?.trim() ?? list[idx].categoria,
      descripcion: patch.descripcion?.trim() ?? list[idx].descripcion,
      metodoPagoLabel:
        patch.metodoPagoLabel !== undefined
          ? patch.metodoPagoLabel?.trim()
          : list[idx].metodoPagoLabel,
      updatedAt: new Date().toISOString(),
    };
    list[idx] = next;
    persistAll(list);
    return next;
  });
  if (updated) emitGastosUpdated();
  return updated;
};

export const anularGastoExterno = (
  id: string,
  motivoAnulacion: string,
): GastoExterno | null => {
  let emit = false;
  const result = runTransaction((): GastoExterno | null => {
    const list = readAllRaw();
    const idx = list.findIndex((g) => g.id === id);
    if (idx === -1) return null;
    if (list[idx].estado === "ANULADO") return list[idx];

    const updated: GastoExterno = {
      ...list[idx],
      estado: "ANULADO",
      anuladoEn: new Date().toISOString(),
      motivoAnulacion: motivoAnulacion.trim(),
      updatedAt: new Date().toISOString(),
    };
    list[idx] = updated;
    persistAll(list);
    emit = true;
    return updated;
  });
  if (emit) emitGastosUpdated();
  return result;
};
